import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextShowMode } from '@/enums/live-context-show-mode.enum';
import { getObjectId } from '@/helpers/object.id.helper';

/** A flow bound to a live entity matches by that binding only, so other event-slot bindings of the same origin stay untouched. */
function flowIsLiveEntity(flow: FlowDto, entityId: string): boolean {
  const bindingId = flow.liveContext?.entityId || flow.liveBindingFlowId;
  if (bindingId) {
    return bindingId === entityId;
  }
  return flow.id === entityId || (flow.origin ? getObjectId(flow.origin) : undefined) === entityId;
}

/** Patch the live show mode into cached flow GET data, which is one document or an array of mode documents. */
export function withFlowLiveContextShowMode(data: unknown, entityId: string, showMode: LiveContextShowMode): unknown {
  const patch = (doc: FlowDto) =>
    doc?.liveContext && doc.liveContext.showMode !== showMode && flowIsLiveEntity(doc, entityId)
      ? { ...doc, liveContext: { ...doc.liveContext, showMode } }
      : doc;
  if (Array.isArray(data)) {
    const next = data.map((doc: FlowDto) => patch(doc));
    return next.some((doc, index) => doc !== data[index]) ? next : data;
  }
  return data && typeof data === 'object' ? patch(data as FlowDto) : data;
}
